// src/components/Sidebar/Sidebar.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { useModulo } from "../../contexts/ModuloContext";
import { NIVEIS } from "../../config/niveis";
import { temNivelMinimo, temArea, AREAS, pode as podePerm } from "../../utils/permissoes";
import { normalizarEmpresaRef } from "../../utils/empresaScope";

import { db } from "../../config/firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import "./Sidebar.css";

// Níveis numéricos
// Gestor - 6
// Administrador - 5
// Técnico Sênior - 4
// Técnico - 3
// Cliente - 2
// Kiosk - 1
// Dashboard - 0

function Sidebar({ isOpen, onClose, isMobile = false }) {
  const [openSection, setOpenSection] = useState(null);
  const navigate = useNavigate();
  const { usuario, logout } = useAuth();
  const { activeArea, allowedAreas, setActiveArea } = useModulo();
  const { modoGlobal, empresaAtual } = useEmpresa();

  const isKiosk = Number(usuario?.nivel ?? -1) === NIVEIS.KIOSK;

  // Determina se o usuário tem mais de uma empresa disponível.
  // - Acesso restrito: base + empresasPermitidas
  // - Acesso global: conta empresas do grupo (Firestore)
  const isGlobalAccess = !!(
    usuario?.acessoGlobal ||
    usuario?.globalAccess ||
    usuario?.perfil?.acessoGlobal ||
    usuario?.tipoAcesso === "GLOBAL" ||
    usuario?.acesso === "GLOBAL" ||
    usuario?.perfil?.tipoAcesso === "GLOBAL"
  );
  const [empresasGrupoCount, setEmpresasGrupoCount] = useState(null);

  const empresasDoUsuario = useMemo(() => {
    if (!usuario) return [];

    const base = [];
    const baseEmpresa = normalizarEmpresaRef({
      id: usuario.empresaId ?? usuario.empresaID ?? null,
      nome: usuario.empresaNome ?? usuario.empresa ?? usuario?.perfil?.empresa ?? null,
    });
    if (baseEmpresa) base.push(baseEmpresa);

    const extras = Array.isArray(usuario.empresasPermitidas) ? usuario.empresasPermitidas : [];
    extras.forEach((raw) => {
      const emp = normalizarEmpresaRef(raw);
      if (!emp) return;
      const jaTem = base.some((b) => (emp.id && b.id ? String(b.id) === String(emp.id) : String(b.nome).trim() === String(emp.nome).trim()));
      if (!jaTem) base.push(emp);
    });

    return base;
  }, [usuario]);

  useEffect(() => {
    let alive = true;

    // Só precisa consultar o grupo quando o usuário tem acesso global
    if (!usuario?.grupoId || !isGlobalAccess) {
      setEmpresasGrupoCount(null);
      return;
    }

    (async () => {
      try {
        const q = query(collection(db, "empresas"), where("grupoId", "==", usuario.grupoId));
        const snap = await getDocs(q);
        if (!alive) return;
        setEmpresasGrupoCount(snap.size);
      } catch (e) {
        console.warn("Falha ao contar empresas do grupo:", e);
        if (!alive) return;
        setEmpresasGrupoCount(null);
      }
    })();

    return () => {
      alive = false;
    };
  }, [usuario?.grupoId, isGlobalAccess]);

  const podeMudarEmpresa = !isKiosk && (
    (empresasDoUsuario.length > 1) ||
    (isGlobalAccess && (empresasGrupoCount ?? 0) > 1)
  );

  // Vamos sempre usar o helper centralizado (já trata usuario.perfil)
  const pode = (nivelMinimo) => temNivelMinimo(usuario, nivelMinimo);
  const podeArea = (area) => temArea(usuario, area);

  const canOpcoes = podePerm(usuario, "gerenciar", "opcoes", "acessa");

  // Permissões dinâmicas (matriz) para exibir o menu conforme Opções > Permissões
  const CADASTROS_RESOURCES = [
    "usuarios",
    "cooperadores",
    "defeitos",
    "empresas",
    "setores",
    "maquinas",
    "status",
    "prestadores",
    "fornecedores",
    "pecas",
    "procedimentos",
    "preventivas",
    "checklists",
  ];
const canCadastros = useMemo(() => {
    if (!usuario) return false;
    return CADASTROS_RESOURCES.some((r) => (
      podePerm(usuario, "cadastros", r, "tabela") ||
      podePerm(usuario, "cadastros", r, "form") ||
      podePerm(usuario, "cadastros", r, "altera")
    ));
  }, [usuario]);

const canPreventivasOperacional = useMemo(() => {
  if (!usuario) return false;
  return (
    podePerm(usuario, "preventivas", "abertas", "tabela") ||
    podePerm(usuario, "preventivas", "finalizadas", "tabela") ||
    podePerm(usuario, "preventivas", "calendario", "tabela") ||
    podePerm(usuario, "preventivas", "execucao", "iniciarExecucao")
  );
}, [usuario]);

const canChecklistsOperacional = useMemo(() => {
  if (!usuario) return false;
  return (
    podePerm(usuario, "checklists", "abertas", "tabela") ||
    podePerm(usuario, "checklists", "finalizadas", "tabela") ||
    podePerm(usuario, "checklists", "calendario", "tabela") ||
    podePerm(usuario, "checklists", "execucao", "iniciarExecucao")
  );
}, [usuario]);

const canPlanejamento = useMemo(() => {
  if (!usuario) return false;
  return (
    podePerm(usuario, "planejamento", "calendario", "tabela") ||
    podePerm(usuario, "planejamento", "atendimentosVisitas", "tabela")
  );
}, [usuario]);

  const canOrdensServico = useMemo(() => {
    if (!usuario) return false;
    return (
      podePerm(usuario, "ordensServico", "porStatus", "tabela") ||
      podePerm(usuario, "ordensServico", "abrirNovaOS", "form") ||
      podePerm(usuario, "ordensServico", "meusAtendimentos", "tabela") ||
      podePerm(usuario, "ordensServico", "periodicidade", "tabela")
    );
  }, [usuario]);

  const canEstoque = useMemo(() => {
    if (!usuario) return false;
    return (
      podePerm(usuario, "estoque", "estoqueInterno", "tabela") ||
      podePerm(usuario, "estoque", "estoqueInterno", "altera") ||
      podePerm(usuario, "estoque", "estoqueInterno", "deleta") ||
      podePerm(usuario, "estoque", "pedidosMaterial", "tabela") ||
      podePerm(usuario, "estoque", "pedidosMaterial", "form") ||
      podePerm(usuario, "estoque", "pedidosMaterial", "altera")
    );
  }, [usuario]);

  const canGerenciar = useMemo(() => {
    if (!usuario) return false;
    return (
      // Opções (Aparência/IA/Relatórios/Permissões)
      canOpcoes ||
      podePerm(usuario, "gerenciar", "dashboard", "acessa") ||
      podePerm(usuario, "gerenciar", "tabelaOS", "tabela") ||
      podePerm(usuario, "gerenciar", "tabelaAtendimentos", "tabela") ||
      podePerm(usuario, "gerenciar", "importarExportar", "acessa")
    );
  }, [usuario, canOpcoes]);

const canKpisManut = useMemo(() => {
  if (!usuario) return false;
  return (
    podePerm(usuario, "kpis", "mtbf", "tabela") ||
    podePerm(usuario, "kpis", "mttr", "tabela") ||
    podePerm(usuario, "kpis", "tecnicos", "tabela") ||
    podePerm(usuario, "kpis", "custoManutencao", "tabela") ||
    podePerm(usuario, "kpis", "outros", "tabela")
  );
}, [usuario]);

  const canCadastrosItem = (recurso) => (
    podePerm(usuario, "cadastros", recurso, "tabela") ||
    podePerm(usuario, "cadastros", recurso, "form") ||
    podePerm(usuario, "cadastros", recurso, "altera")
  );

  const toggleSection = (section) => {
    setOpenSection((prev) => (prev === section ? null : section));
  };

  const goTo = (destino) => {
    if (!destino) return;
    navigate(destino);
  };

  const handleSair = async () => {
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Erro ao sair:", error);
    }
  };

  const nomeUsuario = usuario?.nome || usuario?.displayName || usuario?.email || "Usuário";
  const grupo = usuario?.grupoNome || usuario?.grupo || usuario?.grupoId || "Grupo";
  const grupoLabel = usuario?.grupoNome || usuario?.grupo || "Ramalho Profit";

  const labelArea = (area) => {
    const a = String(area || "").toUpperCase();
    if (a === AREAS.FINANCEIRO) return "Financeiro";
    return "Manutenção";
  };

  // Texto da empresa exibido no topo da sidebar
  const empresaBase = usuario?.empresa || usuario?.perfil?.empresa || "Ramalho Profit";
  const textoEmpresa = modoGlobal
    ? `Global (${grupoLabel})`
    : (empresaAtual?.nome || empresaBase);

  return (
    <aside className={`sidebar ${isOpen ? "sidebar-aberta" : ""}`}>
      {/* Botão fechar mobile */}
      {onClose && (
        <button className="sidebar-close-btn" onClick={onClose} aria-label="Fechar menu">
          ✕
        </button>
      )}
      
      {/* TOPO: usuário/empresa */}
      <div className="sidebar-top">
        <div className="user-info">
          <p>
            <strong>Usuário:</strong> {nomeUsuario}
          </p>
          <p>
            <strong>Grupo:</strong> {grupo}
          </p>
          <p>
            <strong>Cliente:</strong> {textoEmpresa}
          </p>
          {podeMudarEmpresa && (
            <button
              className="btn-empresa"
              type="button"
              onClick={() => goTo("/gerenciar/mudar-cliente")}
            >
              Mudar Cliente
            </button>
          )}
          {canOpcoes && (
            <button
              className="btn-opcoes"
              type="button"
              onClick={() => goTo("/gerenciar/opcoes")}
            >
              Opções
            </button>
          )}
        </div>
      </div>

      {/* MENU PRINCIPAL */}
      <nav className="sidebar-menu">
        <ul>
          {/* =================== CADASTROS =================== */}
          {canCadastros && podeArea(AREAS.MANUTENCAO) && activeArea === AREAS.MANUTENCAO && (
            <li>
              <strong onClick={() => toggleSection("cadastros")}>
                Cadastros
              </strong>
              <ul
                className={`submenu ${
                  openSection === "cadastros" ? "aberto" : ""
                }`}
              >
                {/* Usuários – Tabela: 3,4,5,6 */}
                {canCadastrosItem("usuarios") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/cadastros/usuarios")}
                    >
                      Usuários
                    </button>
                  </li>
                )}

                {/* Clientes – tudo 6 */}
                {canCadastrosItem("empresas") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/cadastros/clientes")}
                    >
                      Clientes
                    </button>
                  </li>
                )}

                {/* Academias – Tabela: 3,4,5,6 */}
                {canCadastrosItem("setores") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/cadastros/setores")}
                    >
                      Academias
                    </button>
                  </li>
                )}

                {/* Equipamentos – Tabela: 3,4,5,6 */}
                {canCadastrosItem("maquinas") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/cadastros/equipamentos")}
                    >
                      Equipamentos
                    </button>
                  </li>
                )}

                {/* Defeitos – Tabela: 3,4,5,6 */}
                {canCadastrosItem("defeitos") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/cadastros/defeitos")}
                    >
                      Defeitos
                    </button>
                  </li>
                )}

                {/* Preventivas – Tabela: 4,5,6 */}
                {canCadastrosItem("preventivas") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/cadastros/preventivas")}
                    >
                      Preventivas
                    </button>
                  </li>
                )}

                {/* Checklists – Tabela: 4,5,6 */}
                {canCadastrosItem("checklists") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/cadastros/checklists")}
                    >
                      Checklists
                    </button>
                  </li>
                )}

                {/* Fornecedores – Tabela: 4,5,6 */}
                {canCadastrosItem("fornecedores") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/cadastros/fornecedores")}
                    >
                      Fornecedores
                    </button>
                  </li>
                )}

		              {/* Prestadores (Serviço) – Tabela: 4,5,6 */}
		              {canCadastrosItem("prestadores") && (
		                <li>
		                  <button
		                    type="button"
		                    className="sidebar-link"
		                    onClick={() => goTo("/cadastros/prestadores")}
		                  >
		                    Prestadores (Serviço)
		                  </button>
		                </li>
		              )}

                {/* Cooperadores (Manutenção) – Tabela: 4,5,6 */}
                {canCadastrosItem("cooperadores") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/cadastros/cooperadores")}
                    >
                      Cooperadores (Manutenção)
                    </button>
                  </li>
                )}

		              {/* Peças – Tabela: 3,4,5,6 */}
                {canCadastrosItem("pecas") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/cadastros/pecas")}
                    >
                      Peças
                    </button>
                  </li>
                )}

                {/* Procedimentos – Tabela: 3,4,5,6 */}
                {canCadastrosItem("procedimentos") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/cadastros/procedimentos")}
                    >
                      Procedimentos
                    </button>
                  </li>
                )}

                {/* Status – Tabela: 5,6 */}
                {canCadastrosItem("status") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/cadastros/status")}
                    >
                      Status
                    </button>
                  </li>
                )}
              </ul>
            </li>
          )}

          {/* =================== ORDENS DE SERVIÇO =================== */}
          {canOrdensServico && podeArea(AREAS.MANUTENCAO) && activeArea === AREAS.MANUTENCAO && (
            <li>
              <strong onClick={() => toggleSection("os")}>
                Ordens de Serviço
              </strong>
              <ul
                className={`submenu ${
                  openSection === "os" ? "aberto" : ""
                }`}
              >
                {/* Ordens de Serviço – Tabela: 2 (suas) 3,4,5,6 */}
                {podePerm(usuario, "ordensServico", "porStatus", "tabela") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/ordens-servico")}
                    >
                      Ordens de Serviço
                    </button>
                  </li>
                )}

                {/* Abrir Nova OS – Form: 1,2,3,4,5,6 */}
                {podePerm(usuario, "ordensServico", "abrirNovaOS", "form") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() =>
                        goTo("/ordens-servico/abrir-nova-os")
                      }
                    >
                      Abrir Nova OS
                    </button>
                  </li>
                )}

                {/* Meus Atendimentos – 3(Próprias),4(Próprias),5-6(Todos) */}
                {podePerm(usuario, "ordensServico", "meusAtendimentos", "tabela") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() =>
                        goTo("/gerenciar/meus-atendimentos")
                      }
                    >
                      Meus Atendimentos
                    </button>
                  </li>
                )}
              </ul>
            </li>
          )}

          {/* =================== PREVENTIVAS =================== */}
          {canPreventivasOperacional && podeArea(AREAS.MANUTENCAO) && activeArea === AREAS.MANUTENCAO && (
            <li>
              <strong onClick={() => toggleSection("preventivas")}>
                Preventivas
              </strong>
              <ul
                className={`submenu ${
                  openSection === "preventivas" ? "aberto" : ""
                }`}
              >
                {podePerm(usuario, "preventivas", "abertas", "tabela") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/preventivas/abertas")}
                    >
                      Preventivas Abertas
                    </button>
                  </li>
                )}
                {podePerm(usuario, "preventivas", "finalizadas", "tabela") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/preventivas/finalizadas")}
                    >
                      Preventivas Finalizadas
                    </button>
                  </li>
                )}
                {podePerm(usuario, "preventivas", "calendario", "tabela") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/preventivas/calendario")}
                    >
                      Calendário de Preventivas
                    </button>
                  </li>
                )}
              </ul>
            </li>
          )}

          {/* =================== CHECKLISTS =================== */}
          {canChecklistsOperacional && podeArea(AREAS.MANUTENCAO) && activeArea === AREAS.MANUTENCAO && (
            <li>
              <strong onClick={() => toggleSection("checklists")}>Checklists</strong>
              <ul
                className={`submenu ${
                  openSection === "checklists" ? "aberto" : ""
                }`}
              >
                {podePerm(usuario, "checklists", "abertas", "tabela") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/checklists/abertos")}
                    >
                      Checklists Abertos
                    </button>
                  </li>
                )}
                {podePerm(usuario, "checklists", "finalizadas", "tabela") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/checklists/finalizados")}
                    >
                      Checklists Finalizados
                    </button>
                  </li>
                )}
                {podePerm(usuario, "checklists", "calendario", "tabela") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/checklists/calendario")}
                    >
                      Calendário de Checklists
                    </button>
                  </li>
                )}
              </ul>
            </li>
          )}

          {/* =================== PLANEJAMENTO =================== */}
          {canPlanejamento && podeArea(AREAS.MANUTENCAO) && activeArea === AREAS.MANUTENCAO && (
            <li>
              <strong onClick={() => toggleSection("planejamento")}>
                Planejamento
              </strong>
              <ul
                className={`submenu ${
                  openSection === "planejamento" ? "aberto" : ""
                }`}
              >
                {podePerm(usuario, "planejamento", "agendamentos", "tabela") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/planejamento/agendamentos-tecnicos")}
                    >
                      Agendamentos Técnicos
                    </button>
                  </li>
                )}
                {podePerm(usuario, "planejamento", "calendario", "tabela") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/planejamento/calendario")}
                    >
                      Calendário
                    </button>
                  </li>
                )}
                {podePerm(usuario, "planejamento", "atendimentosVisitas", "tabela") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/planejamento/atendimentos-visitas")}
                    >
                      Atendimentos (Visitas)
                    </button>
                  </li>
                )}
                {podePerm(usuario, "cadastros", "maquinas", "tabela") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/cadastros/equipamentos?planejamento=1")}
                    >
                      Equipamentos
                    </button>
                  </li>
                )}
              </ul>
            </li>
          )}


          {/* =================== KPIs =================== */}
          {canKpisManut && podeArea(AREAS.MANUTENCAO) && activeArea === AREAS.MANUTENCAO && (
            <li>
              <strong onClick={() => toggleSection("kpis")}>
                KPIs
              </strong>
              <ul
                className={`submenu ${openSection === "kpis" ? "aberto" : ""}`}
              >{podePerm(usuario, "kpis", "mtbf", "tabela") && (

                <li>
                  <button
                    type="button"
                    className="sidebar-link"
                    onClick={() => goTo("/kpis/mtbf")}
                  >
                    MTBF
                  </button>
                </li>
)}{podePerm(usuario, "kpis", "mttr", "tabela") && (

                <li>
                  <button
                    type="button"
                    className="sidebar-link"
                    onClick={() => goTo("/kpis/mttr")}
                  >
                    MTTR
                  </button>
                </li>
)}{podePerm(usuario, "kpis", "tecnicos", "tabela") && (

                <li>
                  <button
                    type="button"
                    className="sidebar-link"
                    onClick={() => goTo("/kpis/tecnicos")}
                  >
                    % Técnicos
                  </button>
                </li>
)}{podePerm(usuario, "kpis", "custoManutencao", "tabela") && (

                <li>
                  <button
                    type="button"
                    className="sidebar-link"
                    onClick={() => goTo("/kpis/custo-manutencao")}
                  >
                    Custo Manutenção
                  </button>
                </li>
)}{podePerm(usuario, "kpis", "outros", "tabela") && (

                <li>
                  <button
                    type="button"
                    className="sidebar-link"
                    onClick={() => goTo("/kpis/outros")}
                  >
                    Outros
                  </button>
                </li>
)}
              </ul>
            </li>
          )}


          {/* =================== CADASTROS (FINANCEIRO) =================== */}
          {podeArea(AREAS.FINANCEIRO) && activeArea === AREAS.FINANCEIRO && pode(NIVEIS.TECNICO) && (
            <li>
              <strong onClick={() => toggleSection("financeiro-cadastros")}>Cadastros</strong>
              <ul className={`submenu ${openSection === "financeiro-cadastros" ? "aberto" : ""}`}>
                <li>
                  <button
                    type="button"
                    className="sidebar-link"
                    onClick={() => goTo("/financeiro/contas")}
                  >
                    Contas
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="sidebar-link"
                    onClick={() => goTo("/financeiro/cartoes-credito")}
                  >
                    Cartões
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="sidebar-link"
                    onClick={() => goTo("/financeiro/cadastros/valores-servicos")}
                  >
                    Valores de Serviços
                  </button>
                </li>
              </ul>
            </li>
          )}

          {/* =================== FINANCEIRO =================== */}
          {podeArea(AREAS.FINANCEIRO) && activeArea === AREAS.FINANCEIRO && (
            <li>
              <strong onClick={() => toggleSection("financeiro")}>Financeiro</strong>
              <ul className={`submenu ${openSection === "financeiro" ? "aberto" : ""}`}> 
                <li>
                  <button
                    type="button"
                    className="sidebar-link"
                    onClick={() => goTo("/financeiro")}
                  >
                    Início
                  </button>
                </li>
                {pode(NIVEIS.TECNICO) && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/financeiro/lancamentos")}
                    >
                      Lançamentos
                    </button>
                  </li>
                )}
                {pode(NIVEIS.TECNICO) && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/financeiro/receitas")}
                    >
                      Receitas
                    </button>
                  </li>
                )}
                {pode(NIVEIS.TECNICO) && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/financeiro/contas-pagar")}
                    >
                      Contas a Pagar
                    </button>
                  </li>
                )}
              </ul>
            </li>
          )}

          {/* =================== PROPOSTAS (FINANCEIRO) =================== */}
          {podeArea(AREAS.FINANCEIRO) && activeArea === AREAS.FINANCEIRO && pode(NIVEIS.TECNICO) && (
            <li>
              <strong onClick={() => toggleSection("financeiro-propostas")}>Propostas</strong>
              <ul className={`submenu ${openSection === "financeiro-propostas" ? "aberto" : ""}`}>
                <li>
                  <button
                    type="button"
                    className="sidebar-link"
                    onClick={() => goTo("/financeiro/contratos")}
                  >
                    Contratos
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="sidebar-link"
                    onClick={() => goTo("/financeiro/instalacoes")}
                  >
                    Instalações
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="sidebar-link"
                    onClick={() => goTo("/financeiro/preventivas")}
                  >
                    Preventivas
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="sidebar-link"
                    onClick={() => goTo("/financeiro/propostas-os")}
                  >
                    Ordens de Serviço
                  </button>
                </li>
              </ul>
            </li>
          )}

          {/* =================== GERENCIAR (FINANCEIRO) =================== */}
          {podeArea(AREAS.FINANCEIRO) && activeArea === AREAS.FINANCEIRO && pode(NIVEIS.TECNICO) && (
            <li>
              <strong onClick={() => toggleSection("financeiro-gerenciar")}>Gerenciar</strong>
              <ul className={`submenu ${openSection === "financeiro-gerenciar" ? "aberto" : ""}`}>
                <li>
                  <button
                    type="button"
                    className="sidebar-link"
                    onClick={() => goTo("/financeiro/gerenciar-propostas")}
                  >
                    Propostas
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="sidebar-link"
                    onClick={() => goTo("/financeiro/relatorios")}
                  >
                    Relatórios
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="sidebar-link"
                    onClick={() => goTo("/financeiro/fluxo-caixa")}
                  >
                    Fluxo de Caixa
                  </button>
                </li>
              </ul>
            </li>
          )}

          {/* =================== GERENCIAR (MANUTENCAO) =================== */}
          {canGerenciar && podeArea(AREAS.MANUTENCAO) && activeArea === AREAS.MANUTENCAO && (
            <li>
              <strong onClick={() => toggleSection("gerenciar")}>
                Gerenciar
              </strong>
              <ul
                className={`submenu ${
                  openSection === "gerenciar" ? "aberto" : ""
                }`}
              >
                {/* Dashboard */}
                {podePerm(usuario, "gerenciar", "dashboard", "acessa") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/")}
                    >
                      Dashboard
                    </button>
                  </li>
                )}

                {/* Tabela de OS - Oculto no mobile */}
                {!isMobile && podePerm(usuario, "gerenciar", "tabelaOS", "tabela") && (
                  <li className="hide-on-mobile">
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/gerenciar/tabela-os")}
                    >
                      Tabela de OS
                    </button>
                  </li>
                )}

                {/* Tabela de Atendimentos - Oculto no mobile */}
                {!isMobile && podePerm(usuario, "gerenciar", "tabelaAtendimentos", "tabela") && (
                  <li className="hide-on-mobile">
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() =>
                        goTo("/gerenciar/tabela-atendimentos")
                      }
                    >
                      Tabela de Atendimentos
                    </button>
                  </li>
                )}

                {/* Importar / Exportar Dados */}
                {podePerm(usuario, "gerenciar", "importarExportar", "acessa") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/gerenciar/importar-exportar")}
                    >
                      Importar / Exportar Dados
                    </button>
                  </li>
                )}

                {/* Opções agora fica no topo (acesso para todos os usuários) */}
              </ul>
            </li>
          )}

          {/* =================== ESTOQUE =================== */}
          {canEstoque && podeArea(AREAS.MANUTENCAO) && activeArea === AREAS.MANUTENCAO && (
            <li>
              <strong onClick={() => toggleSection("estoque")}>
                Estoque
              </strong>
              <ul
                className={`submenu ${
                  openSection === "estoque" ? "aberto" : ""
                }`}
              >
                {/* Pedidos de material */}
                {podePerm(usuario, "estoque", "pedidosMaterial", "tabela") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/estoque/pedidos-pecas")}
                    >
                      Pedidos de Material
                    </button>
                  </li>
                )}

                {/* Histórico de pedidos (mesma permissão da listagem) */}
                {podePerm(usuario, "estoque", "pedidosMaterial", "tabela") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/estoque/historico-pedidos")}
                    >
                      Histórico de Pedidos
                    </button>
                  </li>
                )}

                {/* Estoque Interno */}
                {podePerm(usuario, "estoque", "estoqueInterno", "tabela") && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link"
                      onClick={() => goTo("/estoque/estoque-interno")}
                    >
                      Estoque Interno
                    </button>
                  </li>
                )}
              </ul>
            </li>
          )}
        </ul>
      </nav>

      {/* RODAPÉ */}
      <div className="sidebar-bottom">

        {Array.isArray(allowedAreas) && allowedAreas.length > 1 && (
  <div className="area-switch">
    <select
      className="area-switch-select"
      value={activeArea || allowedAreas[0]}
      onChange={(e) => {
        const nova = e.target.value;
        setActiveArea(nova);

        const HOME_BY_AREA = {
          [AREAS.MANUTENCAO]: "/",
          [AREAS.FINANCEIRO]: "/financeiro",
        };

        const destino = HOME_BY_AREA[nova] || "/";
        navigate(destino);
      }}
    >
      {allowedAreas.map((a) => (
        <option key={a} value={a}>
          {labelArea(a)}
        </option>
      ))}
    </select>
  </div>
)}

        <button className="btn-sair" type="button" onClick={handleSair}>
          Sair
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;
